/**
 * Pure decision helper for the authenticated-app bootstrap in AuthInit.
 *
 * A brand-new user has no organization yet: `GET /organizations/current/`
 * returns 404 and the profile carries `requires_organization_setup: true`.
 * That 404 is the EXPECTED response for such a user — it must not be treated as
 * an authentication failure (which would log them out), and the org-scoped
 * permissions query can never resolve without an org, so the splash gate must
 * not wait for it. In that state we render the app so `MasterLayout` can show
 * the Create-Organization gate (`InitialOrg`).
 */
export interface AuthBootstrapInput {
  userLoading: boolean
  permissionsLoading: boolean
  hasUserData: boolean
  hasPermissionsData: boolean
  /** `requires_organization_setup` from the user profile. */
  requiresOrgSetup: boolean
  userError: boolean
  permissionsError: boolean
  /** HTTP status of the `/organizations/current/` error, if it errored. */
  orgErrorStatus?: number
}

export type AuthBootstrapDecision =
  | 'splash' // still bootstrapping — keep the splash screen
  | 'ready' // fully bootstrapped — render the app
  | 'setup' // authenticated but no org yet — render so InitialOrg can show
  | 'logout' // genuine auth failure — clear session and return to login

export function resolveAuthBootstrap(input: AuthBootstrapInput): AuthBootstrapDecision {
  const orgMissing = input.orgErrorStatus === 404
  const orgHardError = input.orgErrorStatus !== undefined && !orgMissing

  // A genuine auth/bootstrap failure forces logout. The expected "no org yet"
  // 404 is deliberately excluded here.
  if (input.userError || input.permissionsError || orgHardError) {
    return 'logout'
  }

  // Authenticated but without an organization: let the app render so the
  // Create-Organization gate can take over. Permissions are org-scoped and
  // cannot load yet, so we do not wait for them.
  if ((input.requiresOrgSetup || orgMissing) && input.hasUserData) {
    return 'setup'
  }

  // Fully bootstrapped: profile and permissions both present.
  if (!input.userLoading && !input.permissionsLoading && input.hasUserData && input.hasPermissionsData) {
    return 'ready'
  }

  return 'splash'
}
